import { useEffect, useState } from 'react';
import { listGoodsReceiptLines, listPurchaseOrders } from '../api/purchasing';
import { listItems, listUoms } from '../api/masterData';
import { listWarehouses } from '../api/orgData';
import { useBranch } from '../context/BranchContext';
import { Drawer, DrawerField, EmptyState, ErrorNote, LoadingNote, StatusBadge, tableStyle, tdStyle, thStyle } from './common';
import type { GoodsReceipt, GoodsReceiptLine, Item, PurchaseOrder, Uom, Warehouse } from '../types/domain';

/**
 * Read-only Goods Receipt detail drawer — reused by every screen that
 * lists GR rows (Goods Receipts page, Purchasing dashboard) so "No. GR"
 * is a real, inspectable document everywhere it appears. Self-fetches
 * lines/POs/warehouses/items/UOMs when opened so call sites don't each
 * need to preload master data just to show a drawer.
 */
export function GrDetailDrawer({ gr, open, onClose }: { gr: GoodsReceipt | null; open: boolean; onClose: () => void }) {
  const { activeBranchId } = useBranch();
  const [lines, setLines] = useState<GoodsReceiptLine[] | null>(null);
  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gr || !open) return;
    setLines(null);
    setError(null);
    Promise.all([listGoodsReceiptLines(gr.gr_id), listPurchaseOrders(), listWarehouses(activeBranchId ?? undefined), listItems(), listUoms()])
      .then(([lineRows, poRows, whRows, itemRows, uomRows]) => {
        setLines(lineRows);
        setPos(poRows);
        setWarehouses(whRows);
        setItems(itemRows);
        setUoms(uomRows);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [gr, open, activeBranchId]);

  const itemLabel = (id: string) => {
    const it = items.find((i) => i.item_id === id);
    return it ? `${it.item_code} — ${it.name}` : id;
  };
  const uomLabel = (id: string) => uoms.find((u) => u.uom_id === id)?.code ?? id;
  const poNumber = (id: string) => pos.find((po) => po.po_id === id)?.po_number ?? id;
  const warehouseName = (id: string) => warehouses.find((w) => w.warehouse_id === id)?.name ?? id;

  return (
    <Drawer open={open && !!gr} onClose={onClose} title={gr ? `GR ${gr.gr_number}` : 'Detail GR'}>
      {gr && (
        <div>
          <DrawerField label="No. GR" value={gr.gr_number} />
          <DrawerField label="Status" value={<StatusBadge status={gr.status} />} />
          <DrawerField label="Sumber PO" value={poNumber(gr.po_id)} />
          <DrawerField label="Gudang" value={warehouseName(gr.warehouse_id)} />
          <DrawerField label="Diposting" value={gr.posted_at ? new Date(gr.posted_at).toLocaleString('id-ID') : 'Belum diposting'} />
          <DrawerField label="Dibuat" value={new Date(gr.created_at).toLocaleString('id-ID')} />
          {gr.status === 'POSTED' && <DrawerField label="Referensi Stock Ledger" value="Tercatat di Stock Ledger (lihat halaman Stock Ledger, filter berdasarkan waktu posting)." />}

          <div style={{ marginTop: 16, fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-secondary)', marginBottom: 6 }}>Baris GR</div>
          {error && <ErrorNote text={error} />}
          {!error && lines === null && <LoadingNote text="Memuat baris GR..." />}
          {lines && lines.length === 0 && <EmptyState text="Tidak ada baris pada GR ini." />}
          {lines && lines.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Item</th>
                  <th style={thStyle}>Qty diterima</th>
                  <th style={thStyle}>UOM</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.gr_line_id}>
                    <td style={tdStyle}>{itemLabel(l.item_id)}</td>
                    <td style={tdStyle}>{l.qty_received}</td>
                    <td style={tdStyle}>{uomLabel(l.uom_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Drawer>
  );
}
